// Same-origin fetch clients for the mounted /auth/api mutation endpoints —
// the direct-call pattern: an app POSTs from its own origin (where the SSO
// is mounted) and owns the UX in place, instead of redirecting through the
// SSO's SignOut page or the accounts/switch 302. Plain fetch on purpose: the
// console shell has no react-query, and cloud-ui wraps its own core.

import type { SwitchAccountRequest, UseOrgContextRequest } from './schemas';

/** Revoke every session token and expire the cookies (204). Best-effort by
 * design — a failed revocation must never strand the user mid sign-out; the
 * server-side revoke is itself fail-silent. Callers navigate afterwards. */
export async function signOutRequest(): Promise<void> {
    await fetch('/auth/api/sign-out', { method: 'POST' }).catch(
        () => undefined,
    );
}

/** Re-mint the session for another account — the cookie updates in place
 * (Set-Cookie on this response; the token never touches page JS). Throws on
 * failure so callers keep their own error UX; afterwards refresh data or
 * navigate. */
export async function switchAccountRequest(accountId: string): Promise<void> {
    const body: SwitchAccountRequest = { account_id: accountId };
    const res = await fetch('/auth/api/switch-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        // The backend passes upstream rejections through as {error, message}
        // — surface the message so callers' error UX is actionable.
        let message: string | undefined;
        try {
            message = ((await res.json()) as { message?: string }).message;
        } catch {
            // Non-JSON body: fall through to the generic message.
        }
        throw new Error(message || `Unable to switch account (${res.status})`);
    }
}

/** Select (or exchange) the org context under a preserved enterprise
 * identity: the shared session cookie re-mints for the org account while the
 * enterprise token stays in (or moves to) the enterprise app's own host-only
 * cookie, so enterprise API calls keep authenticating throughout. Unlike
 * switchAccountRequest this never changes who the user is. Throws on failure
 * so callers keep their own error UX. */
export async function useOrgContextRequest(accountId: string): Promise<void> {
    const body: UseOrgContextRequest = { account_id: accountId };
    const res = await fetch('/auth/api/use-org-context', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        let message: string | undefined;
        try {
            message = ((await res.json()) as { message?: string }).message;
        } catch {
            // Non-JSON body: fall through to the generic message.
        }
        throw new Error(
            message || `Unable to select organization (${res.status})`,
        );
    }
}

/**
 * Status codes the SSO sign-in page maps in its STATUS_ERRORS (a banner, or
 * none for `console-reauth`). Producers type against this so a renamed status
 * can't silently render nothing. The sign-in page also suppresses its
 * already-signed-in auto-redirect for these: they arrive WITH a live session
 * (the apps no longer revoke on FORBIDDEN), and bouncing that session back
 * to the app that just refused it would loop.
 */
export const SIGN_IN_ERROR_STATUSES = [
    'cloud-session-expired',
    'enterprise-forbidden',
    'console-forbidden',
    'console-reauth',
] as const;

export type SignInStatus = (typeof SIGN_IN_ERROR_STATUSES)[number];
